import { useCallback, useEffect, useRef, useState } from 'react';
import useHttp from './http.hook';
import {
  flattenProductGroups,
  normalizeProductGroupsResponse,
} from '../utils/productGroups';

export const PRODUCTS_PAGE_SIZE = 12;

export function buildProductsPageableUrl({
  page,
  limit,
  name,
  branchId,
  categoryIds,
  productIds,
}) {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  const trimmed = String(name ?? '').trim();
  if (trimmed) params.set('name', trimmed);
  if (branchId) params.set('branchId', String(branchId));
  if (Array.isArray(categoryIds) && categoryIds.length > 0) {
    params.set('categoryIds', categoryIds.join(','));
  }
  if (Array.isArray(productIds) && productIds.length > 0) {
    params.set('productIds', productIds.join(','));
  }
  return `/operator/products/pageable?${params.toString()}`;
}

function toUniqueIntIds(ids) {
  const seen = new Set();
  const out = [];
  (Array.isArray(ids) ? ids : []).forEach((value) => {
    const id = Number.parseInt(String(value), 10);
    if (!Number.isFinite(id) || seen.has(id)) return;
    seen.add(id);
    out.push(id);
  });
  return out;
}

export async function fetchPageableProductsByIds({
  requestWithMeta,
  productIds,
  branchId = null,
  flatten = true,
  pageSize = 100,
}) {
  const ids = toUniqueIntIds(productIds);
  if (ids.length === 0) {
    return { ok: true, list: [] };
  }

  const all = [];
  let page = 1;
  let totalPages = 1;

  do {
    const url = buildProductsPageableUrl({
      page,
      limit: pageSize,
      branchId,
      productIds: ids,
    });
    const res = await requestWithMeta(url, 'GET');
    if (!res?.ok) {
      return {
        ok: false,
        error: res?.data?.error || res?.data?.message || 'Не удалось загрузить товары',
        list: all,
      };
    }

    const groups = normalizeProductGroupsResponse(res?.data);
    const list = flatten ? flattenProductGroups(groups) : groups;
    all.push(...(list || []));
    totalPages = Number(res?.data?.pagination?.totalPages) || 1;
    page += 1;
  } while (page <= totalPages);

  return { ok: true, list: all };
}

function mergeUniqueProducts(prev, next) {
  const seen = new Set((prev || []).map((p) => String(p?._id ?? p?.id)));
  const extra = (next || []).filter((p) => {
    const id = p?._id ?? p?.id;
    if (id == null) return false;
    const key = String(id);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return [...(prev || []), ...extra];
}

export default function usePageableProducts({
  branchId = null,
  flatten = false,
  pageSize = PRODUCTS_PAGE_SIZE,
  categoryIds = null,
  productIds = null,
  enabled = true,
} = {}) {
  const { requestWithMeta } = useHttp();
  const [nameInput, setNameInput] = useState('');
  const [nameQuery, setNameQuery] = useState('');
  const [page, setPage] = useState(1);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(Boolean(enabled));
  const [loadingMore, setLoadingMore] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: pageSize,
    total: 0,
    totalPages: 0,
  });

  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);
  const loadingMoreRef = useRef(false);
  const pageRef = useRef(1);
  const totalPagesRef = useRef(0);

  pageRef.current = page;
  totalPagesRef.current = pagination.totalPages ?? 0;

  const setName = useCallback((value) => {
    setNameInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      setNameQuery(String(value ?? '').trim());
    }, 300);
  }, []);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const categoryKey = Array.isArray(categoryIds) ? categoryIds.join(',') : '';
  const productIdsKey = Array.isArray(productIds) ? productIds.join(',') : '';

  const fetchPage = useCallback(async (pageNum) => {
    const url = buildProductsPageableUrl({
      page: pageNum,
      limit: pageSize,
      name: nameQuery,
      branchId,
      categoryIds: categoryKey ? categoryKey.split(',').map((id) => Number(id)) : null,
      productIds: productIdsKey ? productIdsKey.split(',').map((id) => Number(id)) : null,
    });
    const res = await requestWithMeta(url, 'GET');
    if (!res?.ok) {
      return {
        ok: false,
        error: res?.data?.error || res?.data?.message || 'Не удалось загрузить товары',
        list: [],
        pagination: {
          page: pageNum,
          limit: pageSize,
          total: 0,
          totalPages: 0,
        },
      };
    }

    const groups = normalizeProductGroupsResponse(res?.data);
    const list = flatten ? flattenProductGroups(groups) : groups;
    const nextPagination = res?.data?.pagination ?? {};
    return {
      ok: true,
      list,
      pagination: {
        page: nextPagination.page ?? pageNum,
        limit: nextPagination.limit ?? pageSize,
        total: nextPagination.total ?? 0,
        totalPages: nextPagination.totalPages ?? 0,
      },
    };
  }, [pageSize, nameQuery, branchId, categoryKey, productIdsKey, flatten, requestWithMeta]);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    const requestId = ++requestIdRef.current;
    loadingMoreRef.current = false;
    setLoadingMore(false);
    setLoading(true);
    setError(null);
    setPage(1);
    setProducts([]);

    (async () => {
      try {
        const result = await fetchPage(1);
        if (cancelled || requestId !== requestIdRef.current) return;
        if (!result.ok) {
          setProducts([]);
          setPagination(result.pagination);
          setError(result.error);
          return;
        }
        setProducts(result.list);
        setPagination(result.pagination);
        setPage(1);
      } catch (_) {
        if (cancelled || requestId !== requestIdRef.current) return;
        setProducts([]);
        setError('Ошибка загрузки');
      } finally {
        if (!cancelled && requestId === requestIdRef.current) {
          setLoading(false);
          setReady(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, fetchPage]);

  const loadMore = useCallback(async () => {
    if (!enabled) return;
    if (loading || loadingMoreRef.current) return;
    const currentPage = pageRef.current;
    const totalPages = totalPagesRef.current;
    if (currentPage >= totalPages) return;

    const requestId = requestIdRef.current;
    loadingMoreRef.current = true;
    setLoadingMore(true);

    try {
      const result = await fetchPage(currentPage + 1);
      if (requestId !== requestIdRef.current) return;
      if (!result.ok) return;
      setProducts((prev) => mergeUniqueProducts(prev, result.list));
      setPagination(result.pagination);
      setPage(currentPage + 1);
    } catch (_) {
      // keep already loaded rows
    } finally {
      if (requestId === requestIdRef.current) {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    }
  }, [enabled, loading, fetchPage]);

  const hasMore = page < (pagination.totalPages || 0);

  return {
    name: nameInput,
    setName,
    page,
    products,
    setProducts,
    loading,
    loadingMore,
    ready,
    error,
    pagination,
    total: pagination.total,
    totalPages: pagination.totalPages,
    hasMore,
    loadMore,
  };
}
